import { RouterState, createStore } from '@oneappexchange/lwc-wired';
import { State } from 'zustand';
import { BUCKETS, IS_DEVELOPMENT, KWL_SEARCH_PRELOAD_PAGE_SIZE } from '~/modules/config';
import { FeatureFlags } from '~/modules/feature_flags';
import { parseFilterParam } from '~/modules/helpers/filterHelper';
import { EXPLORE_BASE_ROUTE, Routes } from '~/modules/routes';
import { routerStore } from '~/modules/stores/routerStore';
import { sanitizeText } from '~/modules/utilities/html_utilities';
import { SearchContext, SearchContextListing, SearchResult, SearchResultListing } from '~/schemas/search-schema-v1';
import { getQueryCount, getSearchResults } from '../services/searchService';
import { logger } from '../utilities/logger';

export type SearchTotals = {
    apps: number | undefined;
    consultants: number;
};

enum SearchType {
    APPS = 'apps',
    CONSULTANTS = 'consultants',
}

type FilterOptions = {
    rating?: string;
    price?: string[];
    category?: string[];
    tech?: string[];
    features?: string[]; // Features & Capabilities
    edition?: string[];
    languages?: string[];
    sfExpertise?: string[];
    industryExpertise?: string[];
    experts?: string[];
    location?: string;
    product?: string;
    impact?: string[];
    industry?: string[];
    persona?: string[];
    labs?: string[];
};

export type FilterParamOptions = keyof FilterOptions;

type RouteParams = {
    keywords?: string;
    type?: SearchType;
    sort?: string;
    rankingInformation?: string;
    expand?: string;
} & { [k in FilterParamOptions]?: string };

export class FilterParams {
    rating?: string;
    price?: string[];
    category?: string[];
    tech?: string[];
    features?: string[]; // Features & Capabilities
    edition?: string[];
    languages?: string[];
    sfExpertise?: string[];
    industryExpertise?: string[];
    experts?: string[];
    location?: string;
    product?: string;
    impact?: string[];
    industry?: string[];
    persona?: string[];
    labs?: string[];

    constructor() {
        return {
            rating: undefined,
            price: undefined,
            category: undefined,
            tech: undefined,
            features: undefined,
            edition: undefined,
            languages: undefined,
            sfExpertise: undefined,
            industryExpertise: undefined,
            experts: undefined,
            location: undefined,
            product: undefined,
            impact: undefined,
            industry: undefined,
            persona: undefined,
            labs: undefined,
        };
    }
}

export type SearchParams = {
    keyword?: string;
    sort?: string;
    type?: string;
    filterParams?: FilterParams;
    rankingInformation?: string;
    isExploreSalesforceLabs?: boolean;
    isExploreAgentforce?: boolean;
    expand?: string[];
    searchQueryId?: string;
    prevSearchQueryId?: string;
};

export interface SearchState extends State {
    searchResults: Map<string, SearchResult>;
    totals: Map<string, SearchTotals>;
    searchParams: SearchParams;
    currentPage: number;
}

const initialSearchParams = () => {
    return { keyword: '', filterParams: new FilterParams(), type: SearchType.APPS };
};

const initialState: SearchState = {
    // searchKey -> SearchResults
    searchResults: new Map<string, SearchResult>(),
    // keyword -> SearchTotals
    totals: new Map<string, SearchTotals>(),
    searchParams: initialSearchParams(),
    currentPage: 0,
};

const initialSearchResult = (): SearchResult => ({
    // @ts-expect-error * Automated Type Error Fix
    id: undefined,
    isLoading: false,
    isError: false,
    isEmpty: false,
    listings: [],
    totalSearchResults: 0,
    // @ts-expect-error * Automated Type Error Fix
    context: undefined,
});

const getFilterString = (searchParams: SearchParams): string => {
    const filterParams = {};
    // @ts-expect-error * Automated Type Error Fix
    for (const [filterKey, value] of Object.entries(searchParams?.filterParams)) {
        if (value) {
            // @ts-expect-error * Automated Type Error Fix
            filterParams[filterKey] = value;
        }
    }
    const orderedFilterParams = Object.keys(filterParams)
        .sort()
        .reduce((obj, key) => {
            // @ts-expect-error * Automated Type Error Fix
            obj[key] = filterParams[key];
            return obj;
        }, {});
    return (
        Object.keys(orderedFilterParams)
            // @ts-expect-error * Automated Type Error Fix
            .map((key) => key + '=' + orderedFilterParams[key])
            .join('&')
    );
};

export const getSearchKey = (searchParams: SearchParams): string => {
    let searchKey = `keywords=${searchParams.keyword}&type=${searchParams.type ? searchParams.type : 'apps'}${searchParams.isExploreAgentforce ? '&agentforce=true' : ''}`;
    if (searchParams.sort) {
        searchKey += `&sort=${searchParams.sort}`;
    }
    if (searchParams.filterParams) {
        const filterString = getFilterString(searchParams);
        if (filterString) {
            searchKey += `&${filterString}`;
        }
    }
    return searchKey;
};

const getTotalsIfNeeded = (
    previousTotals: SearchTotals | undefined,
    keyword: string | undefined,
    type: string,
    oppositeType: string,
    filterParams: FilterParams | undefined,
) => {
    if (!keyword || previousTotals) {
        return [];
    }

    const requests = [getQueryCount(keyword, oppositeType)];

    if (filterParams && Object.keys(filterParams).length > 0) {
        requests.push(getQueryCount(keyword, type));
    }

    return requests;
};

export const selectSearchResults = (state: SearchState, searchKey: string): SearchResult | undefined => {
    return state.searchResults.get(searchKey);
};

export const selectSearchTotals = (state: SearchState, keyword: string): SearchTotals | undefined => {
    return state.totals.get(keyword);
};

export const hasFilters = (filterParams: FilterParams | undefined): boolean => {
    if (filterParams) {
        for (const param in filterParams) {
            if (filterParams[param as keyof FilterParams]) {
                return true;
            }
        }
    }
    return false;
};

export const isKeywordLessWithNoFilters = (state: SearchState): boolean => {
    return !state.searchParams.keyword && !hasFilters(state.searchParams.filterParams);
};

export type SearchEventContext = Omit<SearchContext, 'advancedQuery' | 'contextual'> & SearchContextListing;

export const selectSearchContext = (state: SearchState, searchKey: string, listingId: string): SearchEventContext => {
    if (searchKey && listingId) {
        const searchResult = state.searchResults.get(searchKey);
        // @ts-expect-error * Automated Type Error Fix
        const resultElement: SearchResultListing[] = searchResult.listings.filter(
            (srl: SearchResultListing) => srl.oafId === listingId,
        );
        if (resultElement && resultElement.length === 1) {
            const listingContext = resultElement[0].context;
            return {
                // TODO Test how load more influences this
                // @ts-expect-error * Automated Type Error Fix
                searchQueryUid: searchResult.context.searchQueryUid,
                // @ts-expect-error * Automated Type Error Fix
                queryPipeline: searchResult.context.queryPipeline,
                documentUri: listingContext.documentUri,
                documentUrl: listingContext.documentUrl,
                documentUriHash: listingContext.documentUriHash,
                documentPosition: listingContext.documentPosition,
                collection: listingContext.collection,
                rankingModifier: listingContext.rankingModifier,
                sourceName: listingContext.sourceName,
                // @ts-expect-error * Automated Type Error Fix
                originLevel1: searchResult.context.originLevel1,
                // @ts-expect-error * Automated Type Error Fix
                originLevel2: searchResult.context.originLevel2,
                // @ts-expect-error * Automated Type Error Fix
                language: searchResult.context.language,
                // @ts-expect-error * Automated Type Error Fix
                duration: searchResult.context.duration,
                // @ts-expect-error * Automated Type Error Fix
                numberOfResults: searchResult.context.numberOfResults,
                searchTerm: searchKey,
            } as SearchEventContext;
        }
    }
    // @ts-expect-error * Automated Type Error Fix
    return null;
};

// This is the place where we determine if we should attempt to fetch more results.
export const shouldAttemptFetch = (
    previousResult: SearchResult | undefined,
    page: number,
    pageSize: number,
): boolean => {
    const idealResultCount = (page + 1) * pageSize;
    const previousResultElements = previousResult?.listings || [];
    return (
        !previousResult ||
        previousResult.isError ||
        (!previousResult.isLoading &&
            !previousResult.isEmpty &&
            previousResultElements.length < idealResultCount &&
            previousResultElements.length < previousResult.totalSearchResults)
    );
};

export const selectSearchParams = (state: SearchState): SearchParams => {
    return state.searchParams;
};

export const fromExplorePage = (state: SearchState): boolean => {
    return state.searchParams.keyword === '';
};

export const searchPageSelector = (state: SearchState) => {
    return {
        searchParams: selectSearchParams(state),
        noFiltersNoKeyword: isKeywordLessWithNoFilters(state),
        results: selectSearchResults(state, getSearchKey(state.searchParams)),
        totals: selectSearchTotals(state, state.searchParams.keyword as string),
    };
};

export const exportPageSelector = (state: SearchState) => {
    return {
        searchParams: selectSearchParams(state),
        results: selectSearchResults(state, getSearchKey(state.searchParams)),
    };
};

export const searchStore = createStore(
    initialState,

    (set: SetState<SearchState>, get: GetState<SearchState>) => ({
        setError(key: string, error: string, previousResult: SearchResult | undefined) {
            set(function setError(draft) {
                logger.error(`Search Keyword ${key} has the following error; ${error}`);
                draft.searchResults.set(key, {
                    ...initialSearchResult(),
                    isError: true,
                    listings: previousResult?.listings || [],
                    totalSearchResults: previousResult?.totalSearchResults || 0,
                });
                return draft;
            });
        },
        setLoading(key: string, previousResult: SearchResult | undefined) {
            set(function setLoading(draft) {
                const errorResults: SearchResult = {
                    ...initialSearchResult(),
                    isLoading: true,
                    listings: previousResult?.listings || [],
                    totalSearchResults: previousResult?.totalSearchResults || 0,
                } as SearchResult;
                draft.searchResults.set(key, errorResults);
                return draft;
            });
        },
        async fetchSearchResults(searchParams: SearchParams, page = 0, pageSize = 12) {
            const { keyword, sort, filterParams, rankingInformation, searchQueryId, prevSearchQueryId } = searchParams;
            const type = searchParams.type ? searchParams.type : 'apps';
            const oppositeType = type === 'apps' ? 'consultants' : 'apps';
            const searchKey = getSearchKey(searchParams);
            const previousResult = selectSearchResults(get(), searchKey);
            const previousResultElements = previousResult?.listings || [];
            set(function updateSearchParams(draft: SearchState) {
                draft.searchParams = searchParams;
                draft.currentPage = page;
            });

            // Check if this is an explore page (no keyword)
            const shouldPreload = !keyword && FeatureFlags.FF_DATA_CLOUD_SEARCH_UI;
            const effectivePageSize = shouldPreload ? pageSize * KWL_SEARCH_PRELOAD_PAGE_SIZE : pageSize;
            const effectivePage = shouldPreload ? Math.floor(page / KWL_SEARCH_PRELOAD_PAGE_SIZE) : page;

            if (!shouldAttemptFetch(previousResult, page, pageSize)) {
                return;
            }

            this.setLoading(searchKey, previousResult);

            const requestFilterParams: FilterParams = { ...filterParams };
            if (searchParams.isExploreSalesforceLabs) {
                requestFilterParams.labs = ['salesforceLabs'];
            }

            if (searchParams.isExploreAgentforce && !requestFilterParams.product?.includes('Einstein 1')) {
                requestFilterParams.product = requestFilterParams.product
                    ? `${requestFilterParams.product}, Einstein 1`
                    : 'Einstein 1';
            }

            const previousTotals = keyword ? selectSearchTotals(get(), keyword) : undefined;

            try {
                const requests: Promise<SearchResult | number>[] = [
                    getSearchResults(
                        keyword,
                        type,
                        effectivePage,
                        effectivePageSize,
                        requestFilterParams,
                        sort,
                        rankingInformation === 'true',
                        searchQueryId,
                        prevSearchQueryId,
                    ),
                    ...getTotalsIfNeeded(previousTotals, keyword, type, oppositeType, filterParams),
                ];

                const [searchResult, totalOpposite, total] = (await Promise.all(requests)) as [
                    SearchResult,
                    number | undefined,
                    number | undefined,
                ];

                const result: SearchResult = {
                    ...initialSearchResult(),
                    ...searchResult,
                    listings: [...previousResultElements, ...searchResult.listings],
                    isEmpty: searchResult.totalSearchResults === 0,
                } as SearchResult;

                set(function updateSearchResults(draft: SearchState) {
                    draft.searchResults.set(searchKey, result);
                    draft.searchParams.searchQueryId = searchResult?.context?.searchQueryUid;

                    if (keyword && !previousTotals) {
                        draft.totals.set(keyword, {
                            [type]: total || result.totalSearchResults,
                            [oppositeType]: totalOpposite,
                        } as SearchTotals);
                    }
                });
            } catch (error) {
                this.setError(searchKey, error, previousResult);
            }
        },
        setSearchParams(searchParams: SearchParams) {
            set(function setSearchParams(draft: SearchState) {
                draft.searchParams = searchParams;
            });
        },
        setCurrentPage(currentPage: number) {
            set(function setCurrentPage(draft: SearchState) {
                draft.currentPage = currentPage;
            });
        },
        reset() {
            set(function resetStore() {
                return { ...initialState };
            });
        },
    }),
    'searchStore',
    IS_DEVELOPMENT,
);

const getFilterParams = (params: RouteParams | null) => {
    const filterParams = new FilterParams();
    if (params) {
        filterParams.rating = params.rating;
        filterParams.price = parseFilterParam(params.price);
        filterParams.category = parseFilterParam(params.category);
        filterParams.tech = parseFilterParam(params.tech);
        filterParams.features = parseFilterParam(params.features);
        filterParams.edition = parseFilterParam(params.edition);
        filterParams.languages = parseFilterParam(params.languages);
        filterParams.sfExpertise = parseFilterParam(params.sfExpertise);
        filterParams.industryExpertise = parseFilterParam(params.industryExpertise);
        filterParams.experts = parseFilterParam(params.experts);
        filterParams.location = params.location;
        filterParams.product = params.product;
        filterParams.impact = parseFilterParam(params.impact);
        filterParams.industry = parseFilterParam(params.industry);
        filterParams.persona = parseFilterParam(params.persona);
        filterParams.labs = parseFilterParam(params.labs);
    }
    return filterParams;
};

const parseExploreSearchParams = (
    params: RouteParams,
    isExploreSalesforceLabs: boolean = false,
    isExploreAgentforce: boolean = false,
) => {
    const filterParams = getFilterParams(params);
    const searchParams: SearchParams = {
        type: !params.type ? SearchType.APPS : params.type,
        sort: params.sort,
        filterParams,
        isExploreSalesforceLabs,
        isExploreAgentforce,
    };
    searchStore.setSearchParams(searchParams);
};

const parseSearchPageSearchParams = (params: RouteParams) => {
    const keyword = sanitizeText(params.keywords ? params.keywords.trim() : '');
    const filterParams = getFilterParams(params);
    const prevSearchParams = searchStore.getState().searchParams;
    const hasKeywordChanged = keyword !== prevSearchParams.keyword;
    const hasTypeChanged = (params.type || SearchType.APPS) !== prevSearchParams.type;
    const searchQueryId = searchStore.getState().searchParams.searchQueryId;
    const searchParams: SearchParams = {
        keyword: keyword,
        type: !params.type ? SearchType.APPS : params.type,
        sort: params.sort,
        rankingInformation: params.rankingInformation,
        filterParams,
        expand: params.expand?.split(','),
        searchQueryId: hasKeywordChanged || hasTypeChanged ? undefined : searchQueryId,
        prevSearchQueryId: hasKeywordChanged || hasTypeChanged ? searchQueryId : undefined,
    };
    searchStore.setSearchParams(searchParams);
};

routerStore.subscribe(
    ({ url, params }) => {
        if (url.indexOf(EXPLORE_BASE_ROUTE) > -1) {
            const isExploreSalesforceLabs = url.indexOf(BUCKETS.EXPLORE_LABS) > -1;
            const isExploreAgentforce = url.indexOf(BUCKETS.EXPLORE_AGENTFORCE) > -1;
            if (params) {
                parseExploreSearchParams(
                    params as unknown as RouteParams,
                    isExploreSalesforceLabs,
                    isExploreAgentforce,
                );
            } else {
                searchStore.setSearchParams({
                    ...initialSearchParams(),
                    isExploreSalesforceLabs,
                    isExploreAgentforce,
                });
            }
        } else if (url.indexOf(Routes.SEARCH) > -1) {
            parseSearchPageSearchParams((params as unknown as RouteParams) || {});
        } else {
            searchStore.reset();
        }
    },
    (state: RouterState) => ({ url: state.url, params: state.params }),
);
